import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { Trash } from '@phosphor-icons/react';
import PageHeader from '../components/layout/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useAuth } from '../hooks/useAuth';
import { Feedback, getAllFeedback, deleteFeedback } from '../services/feedback';
import usePageTitle from '../hooks/usePageTitle';

const TYPE_COLORS: Record<string, 'error' | 'primary' | 'default'> = {
  bug: 'error',
  feature: 'primary',
  other: 'default',
};

const TYPE_LABELS: Record<string, string> = {
  bug: 'Bug',
  feature: 'Feature',
  other: 'Other',
};

const AdminFeedback: React.FC = () => {
  usePageTitle('Feedback Admin');
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Feedback | null>(null);
  const [errorSnack, setErrorSnack] = useState('');

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getAllFeedback();
        const sorted = [...res.data].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setItems(sorted);
      } catch (err) {
        console.error('Failed to load feedback', err);
        setErrorSnack('Failed to load feedback.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteFeedback(deleteTarget.id);
      setItems((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete feedback', err);
      setErrorSnack('Failed to delete. Please try again.');
      setDeleteTarget(null);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <PageHeader title="Feedback" />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No feedback yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'grey.700', backgroundColor: 'grey.50' } }}>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center" sx={{ width: 56 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                    {new Date(item.created_at).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {item.user_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.user_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={TYPE_LABELS[item.type] ?? item.type}
                      color={TYPE_COLORS[item.type] ?? 'default'}
                      size="small"
                      variant={item.type === 'bug' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography variant="body2" sx={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {item.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 400,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(item)}
                        sx={{ color: 'grey.500', '&:hover': { color: 'error.main' } }}
                      >
                        <Trash size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Feedback"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Snackbar
        open={!!errorSnack}
        autoHideDuration={4000}
        onClose={() => setErrorSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnack('')} variant="filled">
          {errorSnack}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminFeedback;
